import {
  Alert,
  DatePicker,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { ObligationRulesFrequency } from '@prisma/client';
import { FormCard } from '../../../../form-builder/components/new-incorp-layout';
import { FilingFormData } from '../AddCustomFilingModal';
import { ComplianceFrequencyEnumToText } from '../types';

interface FrequencyDueDateStepProps {
  formData: FilingFormData;
  onDataUpdate: (data: Partial<FilingFormData>) => void;
  incorporationDate?: string;
  // Content filings derive frequency + due date from Commenda's content, so the
  // fields are shown read-only for review rather than edited here.
  disabled?: boolean;
  dueDateEditable?: boolean;
}

const frequencyOptions = [
  ObligationRulesFrequency.ONE_TIME,
  ObligationRulesFrequency.MONTHLY,
  ObligationRulesFrequency.BI_MONTHLY,
  ObligationRulesFrequency.QUARTERLY,
  ObligationRulesFrequency.ANNUAL,
  ObligationRulesFrequency.BIENNIAL,
];

export const FrequencyDueDateStep = ({
  formData,
  onDataUpdate,
  incorporationDate,
  disabled = false,
  dueDateEditable = false,
}: FrequencyDueDateStepProps) => {
  const formattedFirstDueDate = formData.firstDueDate
    ? NonTimezoneSensitiveDate.fromISO8601String(formData.firstDueDate).format(
        'MMM D, YYYY'
      )
    : '';

  return (
    <div className="flex flex-col gap-2xl">
      {dueDateEditable ? (
        <Alert
          variant="info"
          description="This filing has no set schedule — choose its due date below."
          descriptionClassName="text-utility-blue-500"
        />
      ) : disabled ? (
        <Alert
          variant="info"
          description="Prefilled for this entity. Review before adding."
          descriptionClassName="text-utility-blue-500"
        />
      ) : null}

      <FormCard>
        <div className="w-full">
          <Label>Filing frequency</Label>
          {disabled ? (
            <div className="mt-sm">
              <Input
                fullWidth
                value={
                  formData.frequency
                    ? ComplianceFrequencyEnumToText[formData.frequency]
                    : ''
                }
                disabled
                readOnly
              />
            </div>
          ) : (
            <Select
              value={formData.frequency}
              onValueChange={(value) =>
                onDataUpdate({
                  frequency: value as FilingFormData['frequency'],
                })
              }
            >
              <SelectTrigger className="mt-sm w-full">
                <SelectValue placeholder="Select a frequency" />
              </SelectTrigger>
              <SelectContent>
                {frequencyOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {ComplianceFrequencyEnumToText[option]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <p className="txt-extra-small-regular text-text-tertiary mt-xs">
            How often this entity needs to file this.
          </p>
        </div>

        <div className="w-full">
          <Label>First due date</Label>
          <div className="mt-sm">
            {disabled && !dueDateEditable ? (
              <Input
                fullWidth
                value={formattedFirstDueDate}
                disabled
                readOnly
              />
            ) : (
              <DatePicker
                date={formData.firstDueDate}
                setDate={(date) => onDataUpdate({ firstDueDate: date })}
                _triggerClassName="border border-border-primary rounded-lg"
                disabled={
                  incorporationDate
                    ? {
                        before:
                          NonTimezoneSensitiveDate.fromISO8601String(
                            incorporationDate
                          ).toDate(),
                      }
                    : undefined
                }
              />
            )}
          </div>
          <p className="txt-extra-small-regular text-text-tertiary mt-xs">
            {dueDateEditable
              ? "This filing isn't on a fixed schedule, so set its due date manually."
              : "We'll calculate all future due dates from this, based on the frequency."}
          </p>
        </div>
      </FormCard>
    </div>
  );
};
